/**
 * Feuille de brouillon — rendu d'une ligne en écriture mathématique
 * (fractions empilées). Avec `tag`, chaque bout devient une cible : toucher un
 * bout d'une ligne du dessus y place le curseur de recopie.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { integralBoundsOf, isDiffAtom, isSumAtom, sumBoundsOf } from '@jeraisonne/reasoner';
import { cn } from '@jeraisonne/ui';
import { atomsTree, lineTree, type MathItem } from './math-layout';
import { disp, gapBefore, type LineItem } from './line-items';

/**
 * Feuille Python (moteur v52) : « [1, 3] » est une LISTE, jamais une matrice. Posé par la
 * feuille et les aperçus d'une trace Python ; faux ailleurs.
 */
export const MathListsContext = createContext(false);

export interface MathTag {
  /** bout sous le curseur de recopie (n° du bout dans la ligne) */
  active: number | null;
}

function edge(it: MathItem, last: boolean): string {
  if (it.kind === 't') return it.t;
  // un exposant se colle à ce qui le précède, comme « ² »
  if (it.kind === 's') return '²';
  if (it.kind === 'g' || it.kind === 'b' || it.kind === 'm') return last ? ')' : '(';
  return '1';
}

function shown(t: string): string {
  return t === '÷' ? '/' : t;
}

/**
 * Un bout d'intégrale (moteur v55) : le signe ∫ ou le crochet fermé, avec la borne du bas
 * en dessous et celle du haut au-dessus (« ∫₀¹ », « ]₀¹ »).
 */
function limitsNode(
  t: string,
  b: { kind: 'int' | 'brk'; lo: string; hi: string },
  key: string,
  props: { className?: string; 'data-k'?: string },
): ReactNode {
  const label =
    b.kind === 'int'
      ? `intégrale de ${disp(b.lo)} à ${disp(b.hi)}`
      : `crochet fermé, de ${disp(b.lo)} à ${disp(b.hi)}`;
  return (
    <span
      key={key}
      {...props}
      className={cn('sc-lim', props.className)}
      data-lim={b.kind}
      role="img"
      aria-label={label}
      title={t}
    >
      <span className="sc-lsign" aria-hidden>
        {b.kind === 'int' ? '∫' : ']'}
      </span>
      <span className="sc-lb" aria-hidden>
        <span className="sc-lhi">{disp(b.hi)}</span>
        <span className="sc-llo">{disp(b.lo)}</span>
      </span>
    </span>
  );
}

/**
 * Un bout de somme ou de produit (moteur v59) : « Σ » (ou « Π ») avec la borne du haut
 * au-dessus et « k = 1 » en dessous. Le terme général le suit sans passer à la ligne.
 */
function sumNode(
  t: string,
  b: { kind: 'sum' | 'prod'; index: string; lo: string; hi: string },
  key: string,
  props: { className?: string; 'data-k'?: string },
): ReactNode {
  return (
    <span
      key={key}
      {...props}
      className={cn('sc-sum', props.className)}
      data-sum={b.kind}
      role="img"
      aria-label={spokenSum(b)}
      title={t}
    >
      <span className="sc-shi" aria-hidden>
        {disp(b.hi)}
      </span>
      <span className="sc-ssign" aria-hidden>
        {b.kind === 'sum' ? 'Σ' : 'Π'}
      </span>
      <span className="sc-slo" aria-hidden>
        {b.index} = {disp(b.lo)}
      </span>
    </span>
  );
}

function spokenSum(b: { kind: 'sum' | 'prod'; index: string; lo: string; hi: string }): string {
  return `${b.kind === 'sum' ? 'somme' : 'produit'} pour ${b.index} de ${disp(b.lo)} à ${disp(b.hi)}`;
}

function tagProps(owners: (number | undefined)[], tag: MathTag | undefined, extra?: string) {
  const list = owners.filter((o): o is number => o !== undefined);
  if (!tag || !list.length) return extra ? { className: extra } : {};
  const on = tag.active !== null && list.includes(tag.active);
  return {
    className: cn('sc-tk', extra, on && 'sc-on'),
    'data-k': list.join(' '),
  };
}

function renderItems(items: MathItem[], tag: MathTag | undefined, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let prev: string[] = [];
  // dernier jeton touchable : les atomes d'un même bout (3x, BC²) forment une seule cible
  let last: { o: number; parts: string[]; index: number } | null = null;
  // un « ∫ » (moteur v55) ou un « Σ » (moteur v59) et ce qui le suit restent sur la même
  // ligne : début du lot à coller
  let glueFrom: number | null = null;
  items.forEach((it, idx) => {
    const pending = glueFrom;
    emit(it, idx);
    if (pending !== null && glueFrom === pending && out.length > pending + 1) {
      const kept = out.splice(pending, out.length - pending);
      out.push(
        <span key={`${keyBase}.${idx}.nw`} className="sc-nowrap">
          {kept}
        </span>,
      );
      glueFrom = null;
      last = null;
    }
  });
  return out;

  function emit(it: MathItem, idx: number): void {
    const key = `${keyBase}.${idx}`;
    const gap0 = prev.length ? gapBefore(prev, edge(it, false)) : '';
    // « dx » ne passe jamais seul à la ligne, ni la fonction loin de son « ∫ » (moteur v55),
    // ni le terme général loin de son « Σ » (moteur v59)
    const glue =
      (it.kind === 't' && isDiffAtom(it.t)) ||
      integralBoundsOf(prev[prev.length - 1])?.kind === 'int' ||
      isSumAtom(prev[prev.length - 1] ?? '');
    const gap = gap0 === ' ' && glue ? '\u00a0' : gap0;
    const sum = it.kind === 't' ? sumBoundsOf(it.t) : null;
    if (it.kind === 't' && sum) {
      if (gap) out.push(gap);
      last = null;
      const props = tag && it.o !== undefined ? tagProps([it.o], tag) : {};
      glueFrom = out.length;
      out.push(sumNode(it.t, sum, key, props));
      prev = [...prev, edge(it, true)].slice(-2);
      return;
    }
    const lim = it.kind === 't' ? integralBoundsOf(it.t) : null;
    if (it.kind === 't' && lim) {
      if (gap) out.push(gap);
      last = null;
      const props = tag && it.o !== undefined ? tagProps([it.o], tag) : {};
      if (lim.kind === 'int') glueFrom = out.length;
      out.push(limitsNode(it.t, lim, key, props));
      prev = [...prev, edge(it, true)].slice(-2);
      return;
    }
    if (it.kind === 't' && tag && last && it.o !== undefined && last.o === it.o) {
      last.parts.push(gap + shown(it.t));
      out[last.index] = (
        <span key={`${keyBase}.m${last.index}`} {...tagProps([last.o], tag)}>
          {last.parts.join('')}
        </span>
      );
      prev = [...prev, edge(it, true)].slice(-2);
      return;
    }
    if (gap) out.push(gap);
    last = null;
    if (it.kind === 't') {
      if (tag && it.o !== undefined) {
        last = { o: it.o, parts: [shown(it.t)], index: out.length };
        out.push(
          <span key={`${keyBase}.m${out.length}`} {...tagProps([it.o], tag)}>
            {shown(it.t)}
          </span>,
        );
      } else {
        out.push(shown(it.t));
      }
    } else if (it.kind === 'g') {
      out.push(
        tag && it.o1 !== undefined ? (
          <span key={`${key}.o`} {...tagProps([it.o1], tag)}>
            (
          </span>
        ) : (
          '('
        ),
        ...renderItems(it.items, tag, key),
        tag && it.o2 !== undefined ? (
          <span key={`${key}.c`} {...tagProps([it.o2], tag)}>
            )
          </span>
        ) : (
          ')'
        ),
      );
    } else if (it.kind === 'b') {
      const own = (side: 0 | 1) => [it.hidden?.[side] ?? it.o];
      const shell = tag && it.o !== undefined ? tagProps([it.o], tag, 'sc-bpick') : {};
      const left = tag ? tagProps(own(0), tag) : {};
      const right = tag ? tagProps(own(1), tag) : {};
      out.push(
        <span
          key={key}
          {...shell}
          className={cn('sc-binom', shell.className)}
          aria-label={`${plain(it.bottom.items)} parmi ${plain(it.top.items)}`}
        >
          <span {...left} className={cn('sc-bpar', left.className)} aria-hidden>
            (
          </span>
          <span className="sc-bcol" aria-hidden>
            <span className="sc-btop">{renderItems(it.top.items, tag, `${key}t`)}</span>
            <span className="sc-bbot">{renderItems(it.bottom.items, tag, `${key}b`)}</span>
          </span>
          <span {...right} className={cn('sc-bpar', right.className)} aria-hidden>
            )
          </span>
        </span>,
      );
    } else if (it.kind === 'm') {
      out.push(matrixNode(it, tag, key));
    } else if (it.kind === 's') {
      const own = [it.o, it.o1, it.o2];
      const sup = tag ? tagProps(own, tag, 'sc-sup') : { className: 'sc-sup' };
      out.push(
        <sup key={key} {...sup} className={cn('sc-sup', sup.className)}>
          {renderItems(it.items, tag, `${key}s`)}
        </sup>,
      );
    } else {
      const inner = tag && !it.whole ? tag : undefined;
      const numProps = inner && it.num.hidden ? tagProps(it.num.hidden, inner, 'sc-paren') : {};
      const denProps = inner && it.den.hidden ? tagProps(it.den.hidden, inner, 'sc-paren') : {};
      const fracProps =
        tag && it.o !== undefined
          ? tagProps([it.o], tag, it.whole ? 'sc-whole' : 'sc-bar')
          : { className: undefined };
      out.push(
        <span key={key} {...fracProps} className={cn('sc-frac', fracProps.className)}>
          <span {...numProps} className={cn('sc-fnum', numProps.className)}>
            {renderItems(it.num.items, inner, `${key}n`)}
          </span>
          <span {...denProps} className={cn('sc-fden', denProps.className)}>
            {renderItems(it.den.items, inner, `${key}d`)}
          </span>
        </span>,
      );
    }
    prev = [...prev, edge(it, true)].slice(-2);
  }
}

/**
 * Une matrice (moteur v30) : un tableau entre deux grandes parenthèses. Recopiée d'un
 * seul bout, elle forme une seule cible ; sinon chaque coefficient se touche.
 */
function matrixNode(
  it: Extract<MathItem, { kind: 'm' }>,
  tag: MathTag | undefined,
  key: string,
): ReactNode {
  const cols = Math.max(1, ...it.rows.map((r) => r.length));
  const shell = tag && it.whole && it.o1 !== undefined ? tagProps([it.o1], tag, 'sc-mpick') : {};
  const inner = it.whole ? undefined : tag;
  const left = inner && it.o1 !== undefined ? tagProps([it.o1], inner) : {};
  const right = inner && it.o2 !== undefined ? tagProps([it.o2], inner) : {};
  return (
    <span
      key={key}
      {...shell}
      className={cn('sc-mat', shell.className)}
      aria-label={`matrice ${plainMatrix(it)}`}
    >
      <span {...left} className={cn('sc-mpar', left.className)} data-side="l" aria-hidden />
      <span className="sc-mgrid" data-cols={cols} aria-hidden>
        {it.rows.flatMap((row, r) =>
          Array.from({ length: cols }, (_, c) => (
            <span key={`${r}.${c}`} className="sc-mcell">
              {row[c] ? renderItems(row[c]!, inner, `${key}r${r}c${c}`) : null}
            </span>
          )),
        )}
      </span>
      <span {...right} className={cn('sc-mpar', right.className)} data-side="r" aria-hidden />
    </span>
  );
}

function plainMatrix(it: Extract<MathItem, { kind: 'm' }>): string {
  return `[${it.rows.map((r) => r.map(plain).join(', ')).join(' ; ')}]`;
}

/** Texte simple d'un morceau d'arbre (lecteur d'écran). */
function plain(items: MathItem[]): string {
  return items
    .map((it) => {
      if (it.kind === 't') {
        const s = sumBoundsOf(it.t);
        if (s) return spokenSum(s);
        const b = integralBoundsOf(it.t);
        if (b)
          return b.kind === 'int'
            ? `intégrale de ${disp(b.lo)} à ${disp(b.hi)}`
            : `crochet fermé, de ${disp(b.lo)} à ${disp(b.hi)}`;
        return shown(it.t);
      }
      if (it.kind === 'g') return `(${plain(it.items)})`;
      if (it.kind === 'm') return plainMatrix(it);
      if (it.kind === 's') return `puissance ${plain(it.items)}`;
      if (it.kind === 'b') return `(${plain(it.bottom.items)} parmi ${plain(it.top.items)})`;
      return `${plain(it.num.items)} / ${plain(it.den.items)}`;
    })
    .join(' ');
}

/** Une ligne de bouts, en écriture mathématique. */
export function LineMath({ items, tag }: { items: LineItem[]; tag?: MathTag }) {
  const lists = useContext(MathListsContext);
  let nodes: ReactNode[];
  try {
    nodes = renderItems(lineTree(items, { lists }), tag, 'l');
  } catch {
    nodes = [items.map((c) => ('atoms' in c ? disp(c.text) : '')).join(' ')];
  }
  return <span className="sc-mx">{nodes}</span>;
}

/** Des atomes en écriture mathématique (bout contenant une division). */
export function AtomsMath({ atoms }: { atoms: string[] }) {
  const lists = useContext(MathListsContext);
  let nodes: ReactNode[];
  try {
    nodes = renderItems(atomsTree(atoms, { lists }), undefined, 'a');
  } catch {
    nodes = [disp(atoms.join(' '))];
  }
  return <span className="sc-mx">{nodes}</span>;
}
